export interface HexCoord {
  q: number
  r: number
}

export interface ChunkCoord {
  cx: number
  cy: number
}

export enum TerrainType {
  WATER = 'water',
  GRASS = 'grass',
  WHEAT = 'wheat',
  FOREST = 'forest',
  HILL = 'hill',
  STONE = 'stone',
  WOOD = 'wood',
  VILLAGE = 'village',
}

export interface BiomeData {
  terrain: TerrainType
  variant: number
  elevation: number
  moisture: number
}

export interface TerrainTile {
  terrain: TerrainType
  variant: number
}

export interface ForestCluster {
  center: HexCoord
  radius: number
  density: number
}

export interface CastleData {
  _id: string
  worldId: string
  q: number
  r: number
  /** Strategic zone group id from map pack (optional on older API payloads). */
  regionGroupId?: string | null
  ownerId: string
  ownerName: string
  level: number
  flagColor?: number | null
  /** Kingdom `empirePeriod.currentPeriod` (1–6); selects world-map castle art `N.png`. */
  empirePeriodCurrent?: number | null
  allianceId?: string | null
  allianceName?: string | null
  /** Same image as alliance overview / world map top bar (`Alliance.flagUrl`). */
  allianceFlagUrl?: string | null
  /** Server: starter protection — held while the owner is in no alliance; joining one clears it for good. */
  newbieShieldActive?: boolean
  /** Server: timed purchased/defeat shield — ISO expiry while active, else null. Drives the "shield:TIME" countdown. */
  protectionShieldExpiresAt?: string | null
  /** 'item' (purchased, also blocks mainland barbarians) or 'defeat' (auto 8h after losing a defense). */
  protectionShieldSource?: 'item' | 'defeat' | null
  /** Computed: number of hex tiles in castle influence area (set by frontend). */
  areaTiles?: number
  /** Computed: approximate area in km² for display (set by frontend). */
  areaKm2?: number
}

/** `world:castle:battle` — a castle in a subscribed chunk entered (`active`) or left a live hex battle. */
export interface WorldCastleBattleData {
  worldId: string
  q: number
  r: number
  active: boolean
  battleId?: string | null
}

/** Optional flags for world map `addCastle` (bulk load vs single socket event). */
export interface AddCastleOptions {
  /** When adding many castles, set true then call `refreshForestOverlays()` once (Canvas2D / Pixi). */
  skipForestRebuild?: boolean
  /**
   * Canvas2D: during init bulk hydration, skip per-castle `markMapDirty` / terrain patch dirty — caller marks once.
   * Avoids thousands of redundant invalidations (main-thread / INP).
   */
  bulkHydration?: boolean
}

export interface VillageData {
  q: number
  r: number
  variant: number
}

export interface AllianceAreaData {
  _id: string
  allianceId: string
  worldId: string
  tiles: HexCoord[]
  color: number | null
  allianceName: string
}

export interface WorldAttackData {
  attackId: string
  attackerWallet: string
  attackerName: string
  defenderWallet: string
  defenderName: string
  fromQ: number
  fromR: number
  toQ: number
  toR: number
  startedAt: string
  arrivesAt: string
  /** Present when `status === 'returning'`; `arrivesAt` also mirrors this for legacy arrow restore. */
  returnArrivesAt?: string
  travelTimeMs: number
  /**
   * Pending lifecycle. Includes the in-battle statuses (`arrived` … `battle_started`) — those rows are
   * served so the map can restore the siege's combat line, not a moving march.
   */
  status?: string
  attackType?: string
  /** rally_join = gathering at leader castle (no map arrow); march = moving to defender */
  phase?: 'rally_join' | 'march'
  rallyMode?: boolean
  rallyPhaseEndAt?: string
  rallyJoinerWallets?: string[]
  /** Cross-region castle siege polyline — reversed server-side when `status === 'returning'`. */
  marchWaypointHexes?: Array<{ q: number; r: number }>
  /** Leg-based castle rallies: per-wallet inbound marches (traveling only). */
  marchLegs?: Array<{
    legId?: string
    walletAddress?: string
    side?: 'attacker' | 'defender'
    role?: string
    fromQ?: number
    fromR?: number
    toQ?: number
    toR?: number
    arrivesAt?: string
    marchTravelTimeMs?: number
    status?: string
    selectedCharacterIds?: string[]
    characterTemplateIds?: string[]
    abilityPoints?: { totalBudget?: number }
  }>
  /** Per-ally home marches after a siege ends (coords already from = besieged castle, to = ally home). */
  allyReturnMarches?: Array<{
    walletAddress?: string
    side?: 'attacker' | 'defender'
    role?: 'rally_joiner' | 'reinforcement'
    fromQ?: number
    fromR?: number
    toQ?: number
    toR?: number
    returnArrivesAt?: string
    marchMs?: number
    characterTemplateIds?: string[]
  }>
}

export type BarbarianVillageState = 'ACTIVE' | 'DEFEATED' | 'CAPTURED' | 'DESPAWNED'

/** Map/chunk DTO — backward compatible: extra fields optional for older clients. */
export interface BarbarianVillageData {
  _id: string
  worldId: string
  q: number
  r: number
  /** Strategic zone group id from map pack (optional on older API payloads). */
  regionGroupId?: string | null
  /** Unique (per world) village name. */
  name?: string | null
  /** 1–21; same tier as PvE campaign round. */
  level: number
  /** Echo of level for UI (API). */
  pveRound?: number
  state?: BarbarianVillageState
  garrisonPower?: number
  /** Single level-banded reward: type + amount (paid on a successful attack and per day of ownership). */
  rewardType?: 'wheat' | 'bread' | 'wood' | 'stone' | 'iron' | 'usdt'
  rewardAmount?: number
  spawnedAt?: string
  updatedAt?: string
  lastSimulatedAt?: string
  respawnAt?: string | null
  capturedBy?: string | null
  capturedAt?: string | null
  /** Owner wallet once conquered (null = wild NPC village). */
  ownerId?: string | null
  /** Resources granted to the owner each day (one village rewards a single banded resource). */
  dailyProduction?: {
    wheat?: number
    bread?: number
    wood?: number
    stone?: number
    iron?: number
    usdt?: number
  }
  lastDailyProductionAt?: string | null
  /** Stationed defender CharacterInstance ids (owner-controlled PvP defenders). */
  defenders?: Array<{ id: string; order?: number }>
  defenderCount?: number
  /** Owner display + alliance flag (hydrated server-side; present on owned villages). */
  ownerDisplayName?: string | null
  ownerAllianceId?: string | null
  ownerAllianceName?: string | null
  allianceFlagUrl?: string | null
  underAttackBy?: string | null
  underAttackUntil?: string | null
  /** Server outbound march duration — used with locks to restore marching line timing. */
  pveMarchTravelTimeMs?: number | null
  pveMarchWaypointHexes?: Array<{ q: number; r: number }>
}

export interface ChunkDynamicData {
  castles: CastleData[]
  barbarianVillages?: BarbarianVillageData[]
}

/** Axis-aligned rectangle in hex (q, r); inclusive bounds. */
export interface TerrainOverrideRect {
  q0: number
  r0: number
  q1: number
  r1: number
  terrain: TerrainType
  /** Defaults to 0 */
  variant?: number
}

/** Polyline in hex space; each vertex is [q, r]. Width is perpendicular distance in axial units (Euclidean). */
export interface TerrainOverrideRiver {
  path: [number, number][]
  halfWidth: number
  terrain: TerrainType
  variant?: number
}

export interface TerrainOverridePoint {
  q: number
  r: number
  terrain: TerrainType
  variant?: number
}

/**
 * Sparse hand-authored terrain layered on procedural noise. Applied after full procedural logic
 * (rects → rivers → points). Keep small; large worlds should stay mostly procedural.
 */
export interface TerrainWorldOverrides {
  rects?: TerrainOverrideRect[]
  rivers?: TerrainOverrideRiver[]
  points?: TerrainOverridePoint[]
}

export interface WorldConfig {
  worldId: string
  seed: number
  width: number
  height: number
  /** Optional; from API with world config */
  terrainOverrides?: TerrainWorldOverrides
  /**
   * Base URL for world_map_generator static output (no trailing slash).
   * Serves `forest-overlays/chunk/{cx}_{cy}.json` and `river-overlays/chunk/{cx}_{cy}.json`
   * plus optional `world-meta.json`. Example: `/assets/maps/world-pack` or a CDN origin.
   */
  generatedMapPackUrl?: string | null
}

export interface ChunkState {
  coord: ChunkCoord
  loaded: boolean
  loading: boolean
  terrainGenerated: boolean
  dynamicData: ChunkDynamicData | null
}

export interface CameraState {
  x: number
  y: number
  zoom: number
}

// ---------------------------------------------------------------------------
// Performance profiles — adapt to device capabilities
// ---------------------------------------------------------------------------

export type DeviceTier = 'high' | 'medium' | 'low'

export interface PerformanceProfile {
  chunkBuffer: number
  maxCachedChunks: number
  drainBatchSize: number
  buildTimeBudgetMs: number
  evictionDelay: number
  evictionBatchSize: number
  minimapSize: number
  minimapRedrawMs: number
  workerTickDivisor: number
}

/** TW2-style: aggressive limits for 50ms frame target */
export const PERF_HIGH: PerformanceProfile = {
  chunkBuffer: 2,
  maxCachedChunks: 64,
  drainBatchSize: 4,
  buildTimeBudgetMs: 4,
  evictionDelay: 8,
  evictionBatchSize: 6,
  minimapSize: 64,
  minimapRedrawMs: 1200,
  workerTickDivisor: 1,
}

export const PERF_MEDIUM: PerformanceProfile = {
  chunkBuffer: 2,
  maxCachedChunks: 32,
  drainBatchSize: 2,
  buildTimeBudgetMs: 3,
  evictionDelay: 6,
  evictionBatchSize: 4,
  minimapSize: 48,
  minimapRedrawMs: 1500,
  workerTickDivisor: 2,
}

export const PERF_LOW: PerformanceProfile = {
  chunkBuffer: 1,
  maxCachedChunks: 16,
  drainBatchSize: 2,
  buildTimeBudgetMs: 3,
  evictionDelay: 4,
  evictionBatchSize: 3,
  minimapSize: 40,
  minimapRedrawMs: 2500,
  workerTickDivisor: 3,
}

export function detectDeviceTier(): DeviceTier {
  if (typeof navigator === 'undefined') return 'high'

  const ua = navigator.userAgent
  const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)
  const cores = navigator.hardwareConcurrency || 2
  const memory = (navigator as any).deviceMemory as number | undefined

  if (isMobile && (cores <= 4 || (memory !== undefined && memory <= 2))) return 'low'
  if (isMobile || cores <= 4 || (memory !== undefined && memory <= 4)) return 'medium'
  return 'high'
}

export function getPerformanceProfile(tier?: DeviceTier): PerformanceProfile {
  const t = tier ?? detectDeviceTier()
  if (t === 'low') return PERF_LOW
  if (t === 'medium') return PERF_MEDIUM
  return PERF_HIGH
}

// ---------------------------------------------------------------------------
// Layout & rendering constants
// ---------------------------------------------------------------------------

export const CHUNK_SIZE = 32
export const TILE_RENDER_WIDTH = 214
export const TILE_RENDER_HEIGHT = 170

/** Overlay (forest, etc.) render size — from map-overlay.json source; separate from tile size */
export const OVERLAY_RENDER_WIDTH = 150
export const OVERLAY_RENDER_HEIGHT = 150

/** Target fraction of placeable area (effective sprite-area meter). */
export const FOREST_WORLD_COVERAGE = 0.6
/** Multiplier on top of FOREST_WORLD_COVERAGE (stacked / layered forest fill). */
export const FOREST_COVERAGE_TARGET_BOOST = 1.0
/**
 * Scales total forest decal target (chains + singles). Lower = fewer trees.
 * ~0.24 × boost 1.0 ≈ ~20% of original unscaled target (was still heavy at 0.7×1.225).
 */
export const FOREST_COVERAGE_AMOUNT_SCALE = 0.24
/**
 * Each sprite adds this × overlay tile area toward the coverage budget; higher ⇒ fewer sprites for same target.
 */
export const FOREST_COVERAGE_EFFECTIVE_AREA_FRACT = 0.14
/** Area divisor: ~OVERLAY^2 * factor per overlapping group (higher = fewer groups; scales with sprites/group). */
export const FOREST_WORLD_GROUP_AREA_FACTOR = 5.9
/** Base cap on chain groups; scaled by map area in worldForestOverlays. */
export const FOREST_WORLD_MAX_GROUPS = 14000
/**
 * Max extra single-sprite placements on grass after chains (fills open grass; no inter-group spacing).
 */
export const FOREST_MAX_SINGLE_SPRINKLE = 45000
/** Min distance between group anchors (world px) — slight gaps between groups. */
export const FOREST_WORLD_MIN_GROUP_SEP = 210
export const FOREST_WORLD_GROUP_GRID_STEP = 200
/** Hex steps (|dq|+|dr|) from any castle tile where forest decals are forbidden. */
export const FOREST_CASTLE_CLEARANCE_HEX = 2

/** Do not place any forest unless at least this many sprites survive grass/bounds checks (no solo patches). */
export const FOREST_GROUP_MIN_COMMIT_SPRITES = 3
/** Sub-blobs per group (inclusive): organic cluster shape. */
export const FOREST_GROUP_SUB_BLOB_MIN = 2
export const FOREST_GROUP_SUB_BLOB_MAX = 4
/** Forest images per sub-blob (inclusive), overlapping within the blob. */
export const FOREST_SUB_BLOB_IMAGE_MIN = 2
export const FOREST_SUB_BLOB_IMAGE_MAX = 5
export const FOREST_GROUP_SUB_BLOB_ORBIT = 52
export const FOREST_GROUP_NOISE_AMP = 11
export const FOREST_GROUP_NOISE_SCALE = 0.0022

// Flat-top hex grid: cols are 3/4 width apart; odd cols shift down by half a tile
export const HEX_COL_SPACING = TILE_RENDER_WIDTH * 0.75   // 160.6875
export const HEX_ROW_SPACING = TILE_RENDER_HEIGHT          // 170
export const HEX_ROW_OFFSET  = TILE_RENDER_HEIGHT / 2     // 84.75

export const MIN_ZOOM = 0.4
export const MAX_ZOOM = 1
export const DEFAULT_ZOOM = MAX_ZOOM

export const CHUNK_BUFFER = 2
export const VIEWPORT_THROTTLE_MS = 80

/** Target frame time (ms) for world map rendering — <50ms for smooth interaction */
export const TARGET_FRAME_MS = 50

export const TERRAIN_SPRITE_MAP: Record<TerrainType, string[]> = {
  [TerrainType.WATER]: ['pool_1.png', 'pool_2.png', 'pool_3.png', 'map-12.png', 'map-16.png'],
  [TerrainType.GRASS]: ['grass_1.png', 'grass_2.png', 'grass_3.png', 'grass_4.png'],
  [TerrainType.WHEAT]: ['wheat_1.png', 'wheat_2.png', 'wheat_3.png'],
  [TerrainType.FOREST]: ['forest_1.png', 'forest_2.png'],
  [TerrainType.HILL]: ['hill_1.png', 'hill_3.png'],
  [TerrainType.STONE]: ['stone_1.png'],
  [TerrainType.WOOD]: ['wood_1.png'],
  [TerrainType.VILLAGE]: ['village_1.png', 'village_3.png'],
}

export const CASTLE_SPRITE = 'castle.png'
export const VILLAGE_SPRITES = ['village_1.png', 'village_3.png']
